import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import ManageUnitsFleetModal from '@/components/modals/ManageUnitsFleetModal';
import RemoveUnitsFleetModal from '@/components/modals/RemoveUnitsFleetModal';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import {
  getAllFleetUnitsQueryOptions,
  getLatestOperatingHoursQueryOptions,
  queryKeys,
} from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';
import useUnitsTable from '@/tables/useUnitsTable';

const unitsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/')({
  component: FleetUnitsPage,
  errorComponent: ErrorBoundary,
  validateSearch: unitsSearchSchema,
  loader: async (opts) => {
    const { fleetId } = opts.params;
    const units = await opts.context.queryClient.ensureQueryData(getAllFleetUnitsQueryOptions({ fleetId }));
    return units;
  },
});

function FleetUnitsPage() {
  const { fleetId } = useParams({
    from: '/_authenticated/_account/fleets/$fleetId/units/',
  });
  const { page: rawPage } = useSearch({
    from: '/_authenticated/_account/fleets/$fleetId/units/',
  });
  const page = rawPage ?? 1;
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mediaQueryNodes: mq } = useMediaQuery();

  const { fleetAccountUnits, isLoading: isLoadingUnits, userHasPermission, userHasCapability } = useFleetAccount();
  const { data: fleetUnits, isLoading: isLoadingFleetUnits } = useQuery(getAllFleetUnitsQueryOptions({ fleetId }));

  const unitIds = fleetUnits?.items.map((unit) => unit.endCustomerUnitId);
  const filteredUnits = fleetAccountUnits?.filter((unit) => unitIds?.includes(String(unit.endCustomerUnitId)));

  // BDIP rejects the whole batch with 403 if any unit lacks telematics.view (AB#1640822).
  const endCustomerUnitIds = filteredUnits
    .map((unit) => unit.endCustomerUnitId)
    .filter((id) => userHasCapability('unit.telematics.view', id));
  const { data: latestOperatingHours, isLoading: isLoadingHours } = useQuery(
    getLatestOperatingHoursQueryOptions({ endcustomerunitids: endCustomerUnitIds })
  );

  const hasSwDownload = userHasPermission('swdownload.read');

  const { columns } = useUnitsTable({
    latestOperatingHours,
    isLoadingHours,
    showInstallation: hasSwDownload,
  });

  const { mutateAsync: removeUnitsFromFleet, isPending: isRemovingUnits } = useMutation({
    mutationFn: (props: { fleetId: string; fleetUnits: { endCustomerUnitId: string; entityVersion: number }[] }) => {
      return vpcService.Fleet.removeUnitsFromFleet(props);
    },
    retry: false,
    onSuccess: async () => {
      // Removed units are hidden from reads by the service until the API catches up
      await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleetUnits], exact: false });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.getFleetUnitsCount], exact: false });
    },
  });

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  return (
    <Table
      key={mqSize}
      isLoading={isLoadingUnits || isLoadingFleetUnits}
      data={filteredUnits || []}
      columns={columns}
      columnVisibility={{
        chassisIdentifiers: false,
      }}
      pageIndex={page}
      defaultSorting={[
        {
          id: 'unitName',
          desc: false,
        },
      ]}
      onPageChange={(pageIndex) => {
        navigate({
          to: '.',
          search: { page: pageIndex > 1 ? pageIndex : undefined },
          replace: true,
        });
      }}
      onRowClick={(row) => {
        navigate({
          to: '/fleets/$fleetId/units/$endCustomerUnitId/specifications',
          params: {
            fleetId: fleetId,
            endCustomerUnitId: row.endCustomerUnitId.toString(),
          },
        });
      }}
      onMouseOver={(row) =>
        router.preloadRoute({
          to: '/fleets/$fleetId/units/$endCustomerUnitId/specifications',
          params: {
            fleetId: fleetId,
            endCustomerUnitId: row.endCustomerUnitId.toString(),
          },
        })
      }
      actions={
        <ManageUnitsFleetModal
          operation="update"
          preSelectedFleetId={fleetId}
          preExistingUnitIds={fleetUnits?.items.map((unit) => String(unit.endCustomerUnitId)) || []}
        />
      }
      selectedActions={(selectedUnits, clearSelection) => (
        <>
          <RemoveUnitsFleetModal
            isLoading={isLoadingUnits || isLoadingFleetUnits || isRemovingUnits}
            onSuccess={async () => {
              if (!fleetId || !fleetUnits) return;
              const selectedUnitIds = new Set(selectedUnits.map((unit) => String(unit.endCustomerUnitId)));
              const unitsToRemove = fleetUnits.items
                .filter((unit) => selectedUnitIds.has(String(unit.endCustomerUnitId)))
                .map((unit) => ({
                  endCustomerUnitId: String(unit.endCustomerUnitId),
                  entityVersion: unit.entityVersion,
                }));
              await removeUnitsFromFleet({ fleetId, fleetUnits: unitsToRemove });
              clearSelection();
            }}
          />
        </>
      )}
    />
  );
}
